import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RotateCcw, CalendarDays, Clock, FileText, Newspaper } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import LottieDefault from 'lottie-react';
import { useTaraUI } from '@/contexts/TaraContext';
import { useAuth } from '@/hooks/useAuth';
import { useTara } from '@/hooks/useTara';
import { TaraMessages } from './TaraMessages';
import { TaraInput } from './TaraInput';
import taraAnimation from '@/assets/tara-sparkle-burst.json';

// Resolve the component whether the bundler hands us the component directly
// or a module-namespace object ({ default: Lottie }) via CJS/ESM interop.
const Lottie: typeof LottieDefault =
  (LottieDefault as unknown as { default?: typeof LottieDefault }).default ?? LottieDefault;

const RISE = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 320, damping: 26 } },
} as const;

const SUGGESTIONS: { text: string; icon: LucideIcon; tint: string }[] = [
  { text: 'How many casual leaves do employees get per year?', icon: CalendarDays, tint: '#3B82F6' },
  { text: 'What are the office working hours and grace period?', icon: Clock, tint: '#F5A300' },
  { text: 'How do I apply for work from home?', icon: FileText, tint: '#8B5CF6' },
  { text: "What's the latest company news on AndTimes?", icon: Newspaper, tint: '#EC4899' },
];

export function TaraPanel(): JSX.Element {
  const { open, setOpen, pendingPrompt, clearPendingPrompt } = useTaraUI();
  const { user } = useAuth();
  const {
    messages, isStreaming, isWaiting, send, stop, reset,
    voiceReady, refreshVoiceHealth, voicePhase, recordingMs, voiceHint, toggleVoice, cancelRecording, voiceAnalyser,
    isMuted, toggleMute,
  } = useTara();

  // Closing the panel mid-dictation discards the recording (and releases the mic).
  useEffect(() => {
    if (!open) cancelRecording();
  }, [open, cancelRecording]);

  // The mount-time health check can fire before auth is ready — retry on open.
  useEffect(() => {
    if (open && !voiceReady) refreshVoiceHealth();
  }, [open, voiceReady, refreshVoiceHealth]);

  // Auto-send a queued prompt (e.g. from a suggestion pill) once the panel is open.
  useEffect(() => {
    if (open && pendingPrompt && !isStreaming) {
      send(pendingPrompt);
      clearPendingPrompt();
    }
  }, [open, pendingPrompt, isStreaming, send, clearPendingPrompt]);

  const firstName = user?.name?.split(' ')[0] ?? 'there';
  const hasUserMessages = messages.some((m) => m.role === 'user');

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="tara-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] bg-black/25 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />

          <motion.div
            key="tara-panel"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
            className="fixed right-0 top-0 bottom-0 z-[70] w-full sm:w-[460px] flex flex-col shadow-2xl border-l"
            style={{ background: 'var(--color-bg)', borderColor: 'var(--color-border)' }}
          >
            {/* Header */}
            <div className="shrink-0 h-12 sm:h-14 px-3 sm:px-5 flex items-center justify-between border-b" style={{ background: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
              <div className="flex items-center gap-2.5">
                <div
                  className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 shadow-sm"
                  style={{ background: 'linear-gradient(135deg, var(--brand-navy) 0%, var(--brand-navy-mid) 100%)' }}
                >
                  <Lottie animationData={taraAnimation} loop className="w-6 h-6" />
                </div>
                <span className="flex items-center gap-1.5">
                  <span className="text-base font-semibold tracking-tight" style={{ color: 'var(--color-primary)' }}>TARA</span>
                  <span
                    className="text-[9px] font-semibold uppercase px-1.5 py-[3px] rounded-md leading-none select-none"
                    style={{ background: '#E7F3EB', color: '#1F6B35', letterSpacing: '0.08em' }}
                    title="TARA is in beta — answers may occasionally be off"
                  >
                    Beta
                  </span>
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={reset}
                  className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-black/5 transition-colors"
                  style={{ color: 'var(--color-text-muted)' }}
                  title="New conversation"
                >
                  <RotateCcw size={13} />
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-black/5 transition-colors"
                  style={{ color: 'var(--color-text-muted)' }}
                  title="Close"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Waiting bar */}
            <AnimatePresence>
              {isWaiting && (
                <motion.div
                  key="tara-waiting-bar"
                  className="h-[2px] w-full overflow-hidden shrink-0"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  <motion.div
                    className="h-full"
                    style={{
                      width: '300%',
                      background:
                        'linear-gradient(90deg, transparent 0%, var(--brand-orange) 25%, var(--brand-navy) 50%, var(--brand-orange) 75%, transparent 100%)',
                    }}
                    animate={{ x: ['0%', '-33.33%'] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex-1 overflow-y-auto">
              {!hasUserMessages ? (
                <motion.div
                  className="min-h-full flex flex-col justify-center px-5 sm:px-7 py-8"
                  initial="hidden"
                  animate="show"
                  variants={{ hidden: {}, show: { transition: { staggerChildren: 0.07, delayChildren: 0.12 } } }}
                >
                  <motion.h2 variants={RISE} className="text-center text-2xl font-semibold leading-tight">
                    <span style={{ color: 'var(--brand-orange)' }}>Hi {firstName},</span>{' '}
                    <span style={{ color: 'var(--color-primary)' }}>what&rsquo;s on your mind?</span>
                  </motion.h2>
                  <div className="mt-8 flex flex-wrap justify-center gap-2">
                    {SUGGESTIONS.map(({ text, icon: Icon, tint }) => (
                      <motion.button
                        key={text}
                        variants={RISE}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => !isStreaming && send(text)}
                        disabled={isStreaming}
                        className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[13px] transition-colors duration-150 hover:bg-black/[0.04] disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          background: 'var(--color-surface)',
                          borderColor: 'var(--gray-200)',
                          color: 'var(--color-primary)',
                        }}
                      >
                        <Icon size={14} className="shrink-0" style={{ color: tint }} />
                        {text}
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <TaraMessages messages={messages} />
              )}
            </div>

            <div
              className="shrink-0 pt-4"
              style={{ background: 'linear-gradient(to top, var(--color-bg) 0%, var(--color-bg) 60%, transparent 100%)' }}
            >
              <TaraInput
                onSend={send}
                isStreaming={isStreaming}
                onStop={stop}
                autoFocus={open}
                voiceReady={voiceReady}
                voicePhase={voicePhase}
                recordingMs={recordingMs}
                voiceHint={voiceHint}
                onToggleVoice={toggleVoice}
                onCancelVoice={cancelRecording}
                voiceAnalyser={voiceAnalyser}
                isMuted={isMuted}
                onToggleMute={toggleMute}
              />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
